import { FC, useState, useMemo, useCallback } from "react";
import { Spin, Empty, Tag, Tooltip, Button, Badge, message, DatePicker } from "antd";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  Users,
  UserCheck,
  UserX,
  Plus,
  X,
  MapPin,
  Briefcase,
  GripVertical,
} from "lucide-react";
import { useGetBookings } from "~/services/booking";
import { useUpdateServiceTicket } from "~/services/service-ticket/service-ticket.mutations";
import { useGetAllStaff } from "~/services/operation";
import { useGetStaffAvailabilities } from "~/services/staff/staff-availability.queries";
import { TServiceTicket, TStaff } from "~/types";
import { formatFullAddress, capitalize } from "~/utils";
import { formatStaffName } from "~/utils/format-staff-name";
import { useNotification } from "~/hooks";
import { StaffAvailability } from "~/utils/constants";
import dayjs from "dayjs";

interface DailyStaffViewProps {
  projectId: string;
}

interface StaffDayInfo {
  staff: TStaff;
  availability: StaffAvailability | null;
  hasAvailabilityRecord: boolean;
  bookedHours: number;
  availableHours: number;
  note?: string;
  assignedServices: TServiceTicket[];
}

const HOUR_HEIGHT = 60; // px per hour row
const START_HOUR = 6; // 6 AM
const END_HOUR = 20; // 8 PM
const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i);

export const DailyStaffView: FC<DailyStaffViewProps> = ({ projectId }) => {
  const [selectedDate, setSelectedDate] = useState(dayjs().startOf("day"));
  const [selectedServiceForAssign, setSelectedServiceForAssign] =
    useState<TServiceTicket | null>(null);
  const { success, error } = useNotification();

  // Fetch bookings
  const { data: bookingsData, isLoading: bookingsLoading } = useGetBookings({
    projectId,
    pageSize: 500,
  });

  // Fetch all staff
  const { data: staffData, isLoading: staffLoading } = useGetAllStaff({
    getAll: true,
    pageSize: 100,
  });

  // Fetch availability for selected date
  const startOfDay = selectedDate.startOf("day").toISOString();
  const endOfDay = selectedDate.endOf("day").toISOString();

  const { data: availabilityData, isLoading: availabilityLoading } =
    useGetStaffAvailabilities(
      { startDate: startOfDay, endDate: endOfDay, pageSize: 200 },
      true
    );

  const { mutate: updateServiceTicket, isPending: isUpdating } =
    useUpdateServiceTicket();

  // All services from bookings
  const allServices = useMemo(() => {
    if (!bookingsData?.items) return [];
    const services: TServiceTicket[] = [];
    bookingsData.items.forEach((booking) => {
      if (booking.serviceTicket?.length > 0) {
        booking.serviceTicket.forEach((service) => {
          services.push({ ...service, jobTicket: booking });
        });
      }
    });
    return services;
  }, [bookingsData?.items]);

  // Services for the selected date
  const dayServices = useMemo(() => {
    return allServices.filter((s) => {
      const serviceDate = dayjs(s.serviceDatetime || s.startDatetime);
      return serviceDate.isSame(selectedDate, "day");
    });
  }, [allServices, selectedDate]);

  // Unassigned services (no staff or understaffed)
  const unassignedServices = useMemo(() => {
    return dayServices.filter((s) => {
      const staffCount = s.staffs?.length || 0;
      const positionsNeeded =
        s.positions?.reduce((sum, p) => sum + p.quantity, 0) || 0;
      return staffCount === 0 || (positionsNeeded > 0 && staffCount < positionsNeeded);
    });
  }, [dayServices]);

  // Build staff day info with their assigned services
  const staffDayData = useMemo((): StaffDayInfo[] => {
    if (!staffData?.items) return [];

    const availabilityMap = new Map<
      string,
      {
        availability: StaffAvailability;
        bookedHours: number;
        availableHours: number;
        note?: string;
      }
    >();

    if (availabilityData?.items) {
      availabilityData.items.forEach((schedule) => {
        if (schedule.staffId) {
          availabilityMap.set(schedule.staffId, {
            availability:
              schedule.availability || StaffAvailability.FULL_TIME,
            bookedHours: schedule.bookedHours || 0,
            availableHours: schedule.availableHours || 8,
            note: schedule.note,
          });
        }
      });
    }

    // Map services to staff
    const staffServiceMap = new Map<string, TServiceTicket[]>();
    dayServices.forEach((service) => {
      (service.staffs || []).forEach((staff) => {
        const existing = staffServiceMap.get(staff.id) || [];
        existing.push(service);
        staffServiceMap.set(staff.id, existing);
      });
    });

    return staffData.items
      .map((staff) => {
        const availInfo = availabilityMap.get(staff.id);
        const hasRecord = availInfo !== undefined;

        return {
          staff,
          availability: hasRecord ? availInfo.availability : null,
          hasAvailabilityRecord: hasRecord,
          bookedHours: availInfo?.bookedHours || 0,
          availableHours: hasRecord ? availInfo.availableHours || 8 : 0,
          note: availInfo?.note,
          assignedServices: staffServiceMap.get(staff.id) || [],
        };
      })
      .filter((s) => {
        // Show staff that are available or have assignments that day
        if (s.assignedServices.length > 0) return true;
        if (!s.hasAvailabilityRecord) return false;
        if (s.availability === StaffAvailability.UNAVAILABLE) return false;
        return true;
      })
      .sort((a, b) => {
        // Sort: assigned first, then available, then by name
        if (a.assignedServices.length > 0 && b.assignedServices.length === 0) return -1;
        if (b.assignedServices.length > 0 && a.assignedServices.length === 0) return 1;
        return formatStaffName(a.staff).localeCompare(formatStaffName(b.staff));
      });
  }, [staffData?.items, availabilityData?.items, dayServices]);

  // Navigate dates
  const goToDay = (offset: number) => {
    setSelectedDate((prev) => prev.add(offset, "day"));
    setSelectedServiceForAssign(null);
  };

  const goToToday = () => {
    setSelectedDate(dayjs().startOf("day"));
    setSelectedServiceForAssign(null);
  };

  // Quick assign: add staff to selected service
  const handleQuickAssign = useCallback(
    (staffId: string, staffName: string) => {
      if (!selectedServiceForAssign) return;

      const alreadyAssigned = selectedServiceForAssign.staffs?.some(
        (s) => s.id === staffId
      );
      if (alreadyAssigned) {
        message.info(`${staffName} is already assigned to this service.`);
        return;
      }

      updateServiceTicket(
        {
          id: selectedServiceForAssign.id,
          staffIds: [staffId],
        },
        {
          onSuccess: () => {
            success({
              message: "Staff Assigned",
              description: `${staffName} assigned to ${selectedServiceForAssign.serial}.`,
            });
            setSelectedServiceForAssign(null);
          },
          onError: (err) => {
            error({
              message: "Failed to assign",
              description: err.message,
            });
          },
        }
      );
    },
    [selectedServiceForAssign, updateServiceTicket, success, error]
  );

  // Remove staff from a service
  const handleRemoveStaff = useCallback(
    (service: TServiceTicket, staffId: string, staffName: string) => {
      updateServiceTicket(
        {
          id: service.id,
          staffIdsToDelete: [staffId],
        },
        {
          onSuccess: () => {
            success({
              message: "Staff Removed",
              description: `${staffName} removed from ${service.serial}.`,
            });
          },
          onError: (err) => {
            error({
              message: "Failed to remove",
              description: err.message,
            });
          },
        }
      );
    },
    [updateServiceTicket, success, error]
  );

  // Get service time position and height for the timeline
  const getServiceBlock = (service: TServiceTicket) => {
    const start = dayjs(service.startDatetime || service.serviceDatetime);
    const startHour = start.hour() + start.minute() / 60;
    const duration = service.duration || 2;
    const top = Math.max((startHour - START_HOUR) * HOUR_HEIGHT, 0);
    const height = Math.max(duration * HOUR_HEIGHT, 40);
    return { top, height, startHour, duration };
  };

  const getAvailabilityBadge = (info: StaffDayInfo) => {
    if (!info.hasAvailabilityRecord) return null;
    if (info.availability === StaffAvailability.UNAVAILABLE)
      return (
        <Tag color="red" className="m-0 text-xs">
          Off
        </Tag>
      );
    if (info.availability === StaffAvailability.PART_TIME)
      return (
        <Tag color="gold" className="m-0 text-xs">
          PT
        </Tag>
      );
    return (
      <Tag color="green" className="m-0 text-xs">
        FT
      </Tag>
    );
  };

  // Week day buttons for quick nav
  const weekDays = useMemo(() => {
    const start = selectedDate.startOf("week");
    return Array.from({ length: 7 }, (_, i) => start.add(i, "day"));
  }, [selectedDate]);

  const isLoading = bookingsLoading || staffLoading;

  return (
    <div className="h-[calc(100vh-200px)] flex flex-col">
      {/* Date Navigation Header */}
      <div className="bg-white rounded-lg border shadow-sm p-4 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Button
              icon={<ChevronLeft size={16} />}
              onClick={() => goToDay(-1)}
              size="small"
            />
            <h2 className="text-lg font-semibold">
              {selectedDate.format("dddd, MMMM D, YYYY")}
            </h2>
            <Button
              icon={<ChevronRight size={16} />}
              onClick={() => goToDay(1)}
              size="small"
            />
            <DatePicker
              value={selectedDate}
              onChange={(date) => {
                if (date) {
                  setSelectedDate(date.startOf("day"));
                  setSelectedServiceForAssign(null);
                }
              }}
              format="MMM D, YYYY"
              allowClear={false}
              size="small"
              style={{ width: 140 }}
            />
            <Button size="small" onClick={goToToday}>
              Today
            </Button>
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <Briefcase size={14} />
              <span>
                {dayServices.length} service{dayServices.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Users size={14} />
              <span>
                {staffDayData.length} staff available
              </span>
            </div>
            {unassignedServices.length > 0 && (
              <div className="flex items-center gap-1 text-orange-600">
                <UserX size={14} />
                <span>{unassignedServices.length} need staffing</span>
              </div>
            )}
          </div>
        </div>

        {/* Week day quick nav */}
        <div className="flex gap-1">
          {weekDays.map((day) => {
            const isSelected = day.isSame(selectedDate, "day");
            const isToday = day.isSame(dayjs(), "day");
            const dayServiceCount = allServices.filter((s) =>
              dayjs(s.serviceDatetime || s.startDatetime).isSame(day, "day")
            ).length;

            return (
              <button
                key={day.format("YYYY-MM-DD")}
                onClick={() => {
                  setSelectedDate(day);
                  setSelectedServiceForAssign(null);
                }}
                className={`flex-1 py-2 px-3 rounded-lg text-center transition-all ${
                  isSelected
                    ? "bg-blue-600 text-white shadow-sm"
                    : isToday
                    ? "bg-blue-50 text-blue-700 border border-blue-200"
                    : "bg-gray-50 hover:bg-gray-100 text-gray-700"
                }`}
              >
                <div className="text-xs font-medium">
                  {day.format("ddd")}
                </div>
                <div className="text-lg font-bold">{day.format("D")}</div>
                {dayServiceCount > 0 && (
                  <div
                    className={`text-xs ${
                      isSelected ? "text-blue-200" : "text-gray-400"
                    }`}
                  >
                    {dayServiceCount} svc
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content: Staff Columns + Unassigned Services */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Staff Columns Grid */}
        <div className="flex-1 bg-white rounded-lg border shadow-sm overflow-hidden flex flex-col">
          <div className="p-3 border-b bg-gray-50 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Calendar size={16} className="text-gray-500" />
              Staff Schedule
            </div>
            {selectedServiceForAssign && (
              <div className="flex items-center gap-2">
                <Tag color="blue" className="m-0">
                  Assigning: {selectedServiceForAssign.serial}
                </Tag>
                <Button
                  type="text"
                  size="small"
                  icon={<X size={14} />}
                  onClick={() => setSelectedServiceForAssign(null)}
                />
              </div>
            )}
          </div>

          <Spin spinning={isLoading}>
            {staffDayData.length === 0 ? (
              <div className="flex-1 flex items-center justify-center p-12">
                <Empty
                  description={
                    isLoading
                      ? "Loading staff..."
                      : "No available staff for this date"
                  }
                />
              </div>
            ) : (
              <div className="flex-1 overflow-auto">
                {/* Column headers */}
                <div className="flex border-b bg-gray-50 sticky top-0 z-10">
                  {/* Time gutter */}
                  <div className="w-16 min-w-[64px] border-r bg-gray-50" />
                  {/* Staff columns */}
                  {staffDayData.map((info) => (
                    <div
                      key={info.staff.id}
                      className={`min-w-[160px] flex-1 border-r p-2 text-center transition-colors ${
                        selectedServiceForAssign
                          ? "cursor-pointer hover:bg-blue-50"
                          : ""
                      }`}
                      onClick={() => {
                        if (selectedServiceForAssign) {
                          handleQuickAssign(
                            info.staff.id,
                            formatStaffName(info.staff)
                          );
                        }
                      }}
                    >
                      <div className="font-medium text-sm truncate">
                        {formatStaffName(info.staff)}
                      </div>
                      <div className="flex items-center justify-center gap-1 mt-1">
                        {getAvailabilityBadge(info)}
                        {info.staff.driver && (
                          <span className="bg-green-500 text-white px-1 rounded text-xs">
                            D
                          </span>
                        )}
                        {info.staff.lead && (
                          <span className="bg-purple-500 text-white px-1 rounded text-xs">
                            L
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {info.assignedServices.length > 0
                          ? `${info.bookedHours || info.assignedServices.reduce((sum, s) => sum + (s.duration || 0), 0)}h booked`
                          : `${info.availableHours}h open`}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Timeline grid */}
                <div className="flex relative" style={{ height: HOURS.length * HOUR_HEIGHT }}>
                  {/* Time gutter */}
                  <div className="w-16 min-w-[64px] border-r bg-gray-50/50">
                    {HOURS.map((hour) => (
                      <div
                        key={hour}
                        className="border-b text-xs text-gray-400 text-right pr-2 pt-1"
                        style={{ height: HOUR_HEIGHT }}
                      >
                        {dayjs().hour(hour).minute(0).format("h A")}
                      </div>
                    ))}
                  </div>

                  {/* Staff columns with service blocks */}
                  {staffDayData.map((info) => (
                    <div
                      key={info.staff.id}
                      className={`min-w-[160px] flex-1 border-r relative ${
                        selectedServiceForAssign
                          ? "cursor-pointer hover:bg-blue-50/30"
                          : ""
                      }`}
                      onClick={() => {
                        if (selectedServiceForAssign) {
                          handleQuickAssign(
                            info.staff.id,
                            formatStaffName(info.staff)
                          );
                        }
                      }}
                    >
                      {/* Hour grid lines */}
                      {HOURS.map((hour) => (
                        <div
                          key={hour}
                          className="border-b border-gray-100"
                          style={{ height: HOUR_HEIGHT }}
                        />
                      ))}

                      {/* Service blocks */}
                      {info.assignedServices.map((service) => {
                        const block = getServiceBlock(service);
                        const serviceColor = service.isExpoDay
                          ? "purple"
                          : "blue";

                        return (
                          <Tooltip
                            key={service.id}
                            title={
                              <div>
                                <div className="font-medium">
                                  {service.serial}
                                </div>
                                <div>
                                  {capitalize(
                                    service.serviceType.replace(/_/g, " ")
                                  )}
                                </div>
                                <div>
                                  {dayjs(service.startDatetime).format(
                                    "h:mm A"
                                  )}{" "}
                                  - {service.duration}h
                                </div>
                                {service.client?.companyName && (
                                  <div>{service.client.companyName}</div>
                                )}
                                {formatFullAddress(service.location) && (
                                  <div className="text-xs opacity-75">
                                    {formatFullAddress(service.location)}
                                  </div>
                                )}
                              </div>
                            }
                          >
                            <div
                              className={`absolute left-1 right-1 rounded-md border overflow-hidden cursor-default ${
                                serviceColor === "purple"
                                  ? "bg-purple-100 border-purple-300"
                                  : "bg-blue-100 border-blue-300"
                              }`}
                              style={{
                                top: block.top,
                                height: block.height,
                                zIndex: 5,
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="p-1.5 h-full flex flex-col">
                                <div className="flex items-center justify-between">
                                  <span
                                    className={`text-xs font-bold truncate ${
                                      serviceColor === "purple"
                                        ? "text-purple-800"
                                        : "text-blue-800"
                                    }`}
                                  >
                                    {service.serial}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveStaff(
                                        service,
                                        info.staff.id,
                                        formatStaffName(info.staff)
                                      );
                                    }}
                                    className="opacity-0 group-hover:opacity-100 hover:bg-red-100 rounded p-0.5"
                                    title="Remove from service"
                                  >
                                    <X size={10} />
                                  </button>
                                </div>
                                <div
                                  className={`text-xs truncate ${
                                    serviceColor === "purple"
                                      ? "text-purple-700"
                                      : "text-blue-700"
                                  }`}
                                >
                                  {capitalize(
                                    service.serviceType.replace(/_/g, " ")
                                  )}
                                </div>
                                {block.height > 50 && (
                                  <div className="text-xs text-gray-500 mt-0.5 truncate">
                                    {dayjs(service.startDatetime).format(
                                      "h:mm A"
                                    )}
                                    {service.duration
                                      ? ` (${service.duration}h)`
                                      : ""}
                                  </div>
                                )}
                                {block.height > 70 &&
                                  service.client?.companyName && (
                                    <div className="text-xs text-gray-400 mt-0.5 truncate">
                                      {service.client.companyName}
                                    </div>
                                  )}
                              </div>
                            </div>
                          </Tooltip>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Spin>
        </div>

        {/* Unassigned Services Panel (Right Side) */}
        <div className="w-80 min-w-[320px] bg-white rounded-lg border shadow-sm overflow-hidden flex flex-col">
          <div className="p-3 border-b bg-gray-50">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Briefcase size={16} className="text-orange-500" />
              <span>
                Day's Services ({dayServices.length})
              </span>
            </div>
            {unassignedServices.length > 0 && (
              <div className="text-xs text-orange-600 mt-1">
                {unassignedServices.length} need staffing
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {dayServices.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No services this day"
              />
            ) : (
              <div className="space-y-2">
                {dayServices
                  .sort((a, b) => {
                    // Understaffed first
                    const aStaffed = (a.staffs?.length || 0) > 0 ? 1 : 0;
                    const bStaffed = (b.staffs?.length || 0) > 0 ? 1 : 0;
                    if (aStaffed !== bStaffed) return aStaffed - bStaffed;
                    return dayjs(a.startDatetime).diff(dayjs(b.startDatetime));
                  })
                  .map((service) => {
                    const staffCount = service.staffs?.length || 0;
                    const positionsNeeded =
                      service.positions?.reduce(
                        (sum, p) => sum + p.quantity,
                        0
                      ) || 0;
                    const needsStaff =
                      staffCount === 0 ||
                      (positionsNeeded > 0 && staffCount < positionsNeeded);
                    const isSelected =
                      selectedServiceForAssign?.id === service.id;

                    return (
                      <div
                        key={service.id}
                        className={`p-3 rounded-lg border transition-all ${
                          isSelected
                            ? "border-blue-500 bg-blue-50 shadow-sm"
                            : needsStaff
                            ? "border-orange-200 bg-orange-50/50 hover:border-orange-300"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-mono text-sm font-bold text-blue-600">
                            {service.serial}
                          </span>
                          <div className="flex items-center gap-1">
                            {service.isExpoDay && (
                              <Tag
                                color="purple"
                                className="m-0 text-xs"
                              >
                                EXPO
                              </Tag>
                            )}
                            <Tag
                              color={needsStaff ? "orange" : "green"}
                              className="m-0 text-xs"
                            >
                              {staffCount}
                              {positionsNeeded > 0
                                ? `/${positionsNeeded}`
                                : ""}{" "}
                              staff
                            </Tag>
                          </div>
                        </div>

                        <div className="text-sm">
                          {capitalize(
                            service.serviceType.replace(/_/g, " ")
                          )}
                          {service.dayLabel && (
                            <span className="text-gray-400 ml-1">
                              ({service.dayLabel})
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                          <Clock size={10} />
                          {dayjs(service.startDatetime).format("h:mm A")}
                          {service.duration
                            ? ` - ${service.duration}h`
                            : ""}
                        </div>

                        {service.client?.companyName && (
                          <div className="text-xs text-gray-400 mt-0.5 truncate">
                            {service.client.companyName}
                          </div>
                        )}

                        {formatFullAddress(service.location) && (
                          <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5 truncate">
                            <MapPin size={10} />
                            {formatFullAddress(service.location)}
                          </div>
                        )}

                        {/* Assigned staff tags */}
                        {staffCount > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {service.staffs?.slice(0, 4).map((s) => (
                              <Tag
                                key={s.id}
                                color="blue"
                                className="m-0 text-xs"
                              >
                                {formatStaffName(s)}
                              </Tag>
                            ))}
                            {staffCount > 4 && (
                              <Tag color="default" className="m-0 text-xs">
                                +{staffCount - 4}
                              </Tag>
                            )}
                          </div>
                        )}

                        {/* Assign button */}
                        {needsStaff && (
                          <Button
                            type={isSelected ? "primary" : "default"}
                            size="small"
                            icon={
                              isSelected ? (
                                <X size={12} />
                              ) : (
                                <UserCheck size={12} />
                              )
                            }
                            onClick={() =>
                              setSelectedServiceForAssign(
                                isSelected ? null : service
                              )
                            }
                            className="mt-2 w-full"
                            loading={isSelected && isUpdating}
                          >
                            {isSelected
                              ? "Cancel Assignment"
                              : "Assign Staff"}
                          </Button>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
